/**
 * <s-fit-studio>: the Seven Wonders "Atelier fitting" size guide (templates/page.wonders-size-guide.liquid).
 * - Reads every tr[data-fit-size] (blocks/fit-size.liquid: data-label, data-chest, data-length in inches).
 * - Clones <template data-fit-pill-template> into [data-fit-pills] once per size; pills use aria-pressed.
 * - Choosing a size (pill or table row) sets --sx / --sy on [data-fit-figure] so the hoodie outline grows,
 *   counts [data-fit-readout="chest|length"] up to the new value and marks the row [data-active].
 * - [data-fit-unit="in|cm"] buttons switch readouts and table cells between inches and centimetres.
 * - Marks itself [data-fit-ready] (CSS shows the pills and starts the outline draw).
 * Counting and scaling animations are skipped for shoppers who prefer reduced motion.
 */
class SFitStudio extends HTMLElement {
  #ac;
  #rows = [];
  #pills = [];
  #index = 0;
  #unit = 'in';
  #frames = new Map();
  #calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#rows = [...this.querySelectorAll('tr[data-fit-size]')];
    if (!this.#rows.length) return;

    this.#buildPills(opts);

    this.#rows.forEach((row, index) => {
      row.addEventListener('click', () => this.#select(index), opts);
    });

    this.querySelectorAll('[data-fit-unit]').forEach((button) => {
      button.addEventListener('click', () => this.#setUnit(button.dataset.fitUnit), opts);
    });

    const preferred = (this.dataset.defaultSize || 'M').toUpperCase();
    const start = this.#rows.findIndex((row) => row.dataset.label.toUpperCase() === preferred);
    this.#select(start === -1 ? 0 : start, true);
    this.setAttribute('data-fit-ready', '');
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#frames.forEach((id) => cancelAnimationFrame(id));
  }

  #buildPills(opts) {
    const holder = this.querySelector('[data-fit-pills]');
    const template = this.querySelector('template[data-fit-pill-template]');
    if (!holder || !template) return;
    this.#rows.forEach((row, index) => {
      const pill = template.content.firstElementChild.cloneNode(true);
      pill.textContent = row.dataset.label;
      pill.addEventListener('click', () => this.#select(index), opts);
      holder.append(pill);
      this.#pills.push(pill);
    });
  }

  #bounds(key) {
    const values = this.#rows.map((row) => Number(row.dataset[key]) || 0);
    return { min: Math.min(...values), max: Math.max(...values) };
  }

  #select(index, instant = false) {
    this.#index = index;
    const row = this.#rows[index];
    this.#rows.forEach((r, i) => r.toggleAttribute('data-active', i === index));
    this.#pills.forEach((p, i) => p.setAttribute('aria-pressed', String(i === index)));

    const chest = Number(row.dataset.chest) || 0;
    const length = Number(row.dataset.length) || 0;
    const chestRange = this.#bounds('chest');
    const lengthRange = this.#bounds('length');
    const figure = this.querySelector('[data-fit-figure]');
    if (figure) {
      // Smallest size draws at 84%, largest at 100%, so the change is visible but the outline stays in frame.
      const scale = (value, { min, max }) => (max === min ? 1 : 0.84 + (0.16 * (value - min)) / (max - min));
      figure.style.setProperty('--sx', scale(chest, chestRange).toFixed(3));
      figure.style.setProperty('--sy', scale(length, lengthRange).toFixed(3));
    }

    this.querySelectorAll('[data-fit-current]').forEach((el) => (el.textContent = row.dataset.label));
    this.#renderReadouts(instant);
  }

  #convert(inches) {
    return this.#unit === 'cm' ? Math.round(inches * 2.54) : inches;
  }

  #renderReadouts(instant = false) {
    const row = this.#rows[this.#index];
    for (const key of ['chest', 'length']) {
      const el = this.querySelector(`[data-fit-readout="${key}"]`);
      if (!el) continue;
      const target = this.#convert(Number(row.dataset[key]) || 0);
      this.#count(el, target, instant || this.#calm);
    }
  }

  #count(el, target, instant) {
    cancelAnimationFrame(this.#frames.get(el));
    const from = Number(el.textContent) || 0;
    if (instant || from === target) {
      el.textContent = String(target);
      return;
    }
    const startTime = performance.now();
    const duration = 700;
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = String(Math.round(from + (target - from) * eased));
      if (t < 1) this.#frames.set(el, requestAnimationFrame(step));
    };
    this.#frames.set(el, requestAnimationFrame(step));
  }

  #setUnit(unit) {
    if (unit !== 'in' && unit !== 'cm') return;
    this.#unit = unit;
    this.querySelectorAll('[data-fit-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fitUnit === unit)));
    this.querySelectorAll('[data-fit-unit-label]').forEach((el) => {
      el.textContent = unit === 'cm' ? el.dataset.labelCm : el.dataset.labelIn;
    });
    this.querySelectorAll('[data-fit-cell]').forEach((cell) => {
      cell.textContent = String(this.#convert(Number(cell.dataset.inches) || 0));
    });
    this.#renderReadouts();
  }
}

if (!customElements.get('s-fit-studio')) customElements.define('s-fit-studio', SFitStudio);
